/**
 * Edge stream source selection.
 *
 * Each detector carries a direct edge URL (`stream_url`) and a center
 * relay path (`stream_url_proxy`). `resolveStreamUrl` returns the URL the
 * browser should load: the proxy reaches every edge through the center,
 * while the direct URL only works for an edge the browser can reach itself.
 * Reachability is probed once per edge host with a snapshot load; until the
 * probe settles (and whenever it fails or force-proxy is on) the proxy URL
 * is used.
 */
import { URLS } from '../config/urls';
import { toSnapshotUrl } from './stream';

type Decision = 'direct' | 'proxy';

const PROBE_TIMEOUT_MS = 1500;

const decisions = new Map<string, Decision>();
const inflight = new Set<string>();
const listeners = new Set<() => void>();
let version = 0;

const FORCE_PROXY =
  typeof window !== 'undefined' &&
  Boolean((window as unknown as { __STREAM_FORCE_PROXY__?: boolean }).__STREAM_FORCE_PROXY__);

export interface StreamSourceInput {
  stream_url?: string | null;
  stream_url_proxy?: string | null;
}

function notify(): void {
  version += 1;
  for (const cb of listeners) cb();
}

export function subscribeStreamSource(cb: () => void): () => void {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
}

export function getStreamSourceVersion(): number {
  return version;
}

function hostOf(url: string): string | null {
  try {
    return new URL(url).host;
  } catch {
    return null;
  }
}

function toProxyUrl(proxyPath: string): string {
  return `${URLS.STREAM_PROXY}${proxyPath}`;
}

function probe(host: string, directUrl: string): void {
  if (inflight.has(host) || decisions.has(host)) return;
  inflight.add(host);

  const img = new Image();
  let settled = false;
  const finish = (decision: Decision): void => {
    if (settled) return;
    settled = true;
    window.clearTimeout(timer);
    inflight.delete(host);
    decisions.set(host, decision);
    notify();
  };

  const timer = window.setTimeout(() => finish('proxy'), PROBE_TIMEOUT_MS);
  img.onload = () => finish('direct');
  img.onerror = () => finish('proxy');
  img.src = toSnapshotUrl(directUrl);
}

export function resolveStreamUrl(input?: StreamSourceInput | null): string {
  const direct = input?.stream_url || '';
  const proxyPath = input?.stream_url_proxy || '';

  if (!proxyPath) return direct;
  if (!direct) return toProxyUrl(proxyPath);
  if (FORCE_PROXY) return toProxyUrl(proxyPath);

  const host = hostOf(direct);
  if (!host) return toProxyUrl(proxyPath);

  const decision = decisions.get(host);
  if (decision === 'direct') return direct;
  if (decision === 'proxy') return toProxyUrl(proxyPath);

  probe(host, direct);
  return toProxyUrl(proxyPath);
}
